import { useState, useEffect } from 'react';
import { Plus, Send, Trash2, User, DollarSign, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface AirplaneTicketRec {
  id: string;
  driver_name: string;
  amount: number;
  note: string;
  created_at: string;
}

export function AirplaneTicketsRecView() {
  const toast = useToast();
  const [tickets, setTickets] = useState<AirplaneTicketRec[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    driver_name: '',
    amount: '',
    note: ''
  });

  useEffect(() => {
    loadTickets();
    loadDrivers();
  }, []);

  async function loadDrivers() {
    try {
      console.log('Loading drivers...');
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, status')
        .order('first_name');

      if (error) {
        console.error('Error loading drivers:', error);
        throw error;
      }
      
      console.log('Drivers loaded:', data?.length || 0, data);
      setDrivers(data || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    }
  }

  async function loadTickets() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('airplane_tickets_rec')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Failed to load airplane tickets');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!formData.driver_name || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('airplane_tickets_rec')
        .insert([{
          driver_name: formData.driver_name,
          amount: parseFloat(formData.amount),
          note: formData.note
        }]);

      if (error) throw error;

      toast.success('Airplane ticket created successfully');
      setShowAddModal(false);
      setFormData({ driver_name: '', amount: '', note: '' });
      loadTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create airplane ticket');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Are you sure you want to delete this ticket?')) return;

    try {
      const { error } = await supabase
        .from('airplane_tickets_rec')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Ticket deleted successfully');
      loadTickets();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error('Failed to delete ticket');
    }
  }

  const filteredTickets = tickets.filter(ticket =>
    ticket.driver_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.note?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalAmount = filteredTickets.reduce((sum, ticket) => sum + ticket.amount, 0);

  // Calculate monthly analytics
  const monthlyData = tickets.reduce((acc, ticket) => {
    const date = new Date(ticket.created_at);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    if (!acc[monthKey]) {
      acc[monthKey] = { monthName, total: 0, count: 0 };
    }
    acc[monthKey].total += ticket.amount;
    acc[monthKey].count += 1;
    return acc;
  }, {} as Record<string, { monthName: string; total: number; count: number }>);

  // Sort by month descending (most recent first)
  const sortedMonths = Object.entries(monthlyData).sort((a, b) => b[0].localeCompare(a[0]));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Airplane Tickets REC</h1>
          <p className="text-sm text-slate-600 mt-1">Recruiting airplane ticket reimbursements</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Ticket
        </button>
      </div>

      {/* Monthly Analytics */}
      {sortedMonths.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Monthly Spending</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortedMonths.map(([monthKey, data]) => (
              <div key={monthKey} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-slate-700">{data.monthName}</h3>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                    {data.count} {data.count === 1 ? 'ticket' : 'tickets'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-purple-900">${data.total.toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Send className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Tickets</p>
              <p className="text-2xl font-bold text-slate-900">{filteredTickets.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Amount</p>
              <p className="text-2xl font-bold text-slate-900">${totalAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by driver name or note..."
          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Driver Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Note
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-3">
                      <Send className="w-12 h-12 text-slate-300" />
                      <p className="text-lg font-medium">No airplane tickets found</p>
                      <p className="text-sm">Click "Add Ticket" to create a new one</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900">{ticket.driver_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-green-600">${ticket.amount.toFixed(2)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-900">{ticket.note || '-'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-600">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleDelete(ticket.id)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Add Airplane Ticket</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Driver Name */}
              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Driver Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={driverSearchTerm || formData.driver_name}
                  onChange={(e) => {
                    setDriverSearchTerm(e.target.value);
                    setFormData({ ...formData, driver_name: '' });
                  }}
                  onFocus={() => setDriverSearchTerm(formData.driver_name)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search driver..."
                  required
                />
                {driverSearchTerm && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {drivers
                      .filter(driver => 
                        `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(driverSearchTerm.toLowerCase())
                      )
                      .map((driver) => (
                        <button
                          key={driver.id}
                          type="button"
                          onClick={() => {
                            const fullName = `${driver.first_name} ${driver.last_name}`;
                            setFormData({ ...formData, driver_name: fullName });
                            setDriverSearchTerm('');
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors"
                        >
                          {driver.first_name} {driver.last_name}
                        </button>
                      ))}
                    {drivers.filter(driver => 
                      `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(driverSearchTerm.toLowerCase())
                    ).length === 0 && (
                      <div className="px-4 py-2 text-slate-500 text-sm">No drivers found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Note
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Add any notes..."
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({ driver_name: '', amount: '', note: '' });
                    setDriverSearchTerm('');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
